import {createSlice, PayloadAction} from '@reduxjs/toolkit';

// Enhanced Photo interface to include File and Blob references
interface Photo {
  id?: string;
  uri: string;
  fileName?: string;
  fileType?: string;
  isLocal?: boolean;
  file?: File; // Original File object for web uploads
  blob?: Blob; // Cropped blob ready for upload
}

interface PhotosPayload {
  photoUris: string[];
  photos: Photo[];
  primaryPhotoIndex: number;
}

interface Location {
  latitude: number | null;
  longitude: number | null;
  address: string;
}

interface RegistrationState {
  email: string;
  password: string;
  full_name: string;
  birth_date: string;
  gender: string;
  sexual_orientation: string;
  preference: string;
  bio: string;
  photos: string[]; // Keep for backward compatibility
  photoObjects: Photo[]; // Enhanced photo objects
  photoUris: string[]; // Added for consistency with our updates
  primary_photo_index: number;
  primaryPhotoIndex: number; // Added for consistency with our updates
  location: Location;
  step: number;
  isSocialSignup: boolean;
  socialProvider?: string;
}

const initialState: RegistrationState = {
  email: '',
  password: '',
  full_name: '',
  birth_date: '',
  gender: '',
  sexual_orientation: '',
  preference: '',
  bio: '',
  photos: [],
  photoObjects: [],
  photoUris: [],
  primary_photo_index: 0,
  primaryPhotoIndex: 0,
  location: {
    latitude: null,
    longitude: null,
    address: '',
  },
  step: 1,
  isSocialSignup: false,
  socialProvider: undefined,
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    updateEmail: (
      state,
      action: PayloadAction<{email: string; password: string}>,
    ) => {
      state.email = action.payload.email;
      state.password = action.payload.password;
      state.step = Math.max(state.step, 2);
    },
    updateName: (
      state,
      action: PayloadAction<{full_name: string}>,
    ) => {
      state.full_name = action.payload.full_name;
      state.step = Math.max(state.step, 3);
    },
    updateBirthday: (state, action: PayloadAction<string>) => {
      state.birth_date = action.payload;
      state.step = Math.max(state.step, 4);
    },
    updateGender: (state, action: PayloadAction<string>) => {
      state.gender = action.payload;
      state.step = Math.max(state.step, 5);
    },
    updateOrientation: (state, action: PayloadAction<string>) => {
      state.sexual_orientation = action.payload;
    },
    updatePreference: (state, action: PayloadAction<string>) => {
      state.preference = action.payload;
    },
    updateBio: (state, action: PayloadAction<string>) => {
      state.bio = action.payload;
    },
    updateLocation: (state, action: PayloadAction<Location>) => {
      state.location = action.payload;
      state.step = Math.max(state.step, 7);
    },
    updatePhotos: (state, action: PayloadAction<string[] | PhotosPayload>) => {

      if (Array.isArray(action.payload)) {
        // Legacy format: array of URIs
        state.photos = action.payload;
        state.photoUris = action.payload;
        state.photoObjects = action.payload.map(uri => ({
          uri,
          isLocal: true,
          id: `photo-${Date.now()}-${Math.random().toString(36).substring(2)}`,
        }));
      } else {
        // New enhanced format with complete photo data
        const {photoUris, photos, primaryPhotoIndex} = action.payload;


        // Update all photo-related fields for maximum compatibility
        state.photoUris = photoUris;
        state.photos = photoUris;
        state.photoObjects = photos.map(photo => ({
          ...photo,
          isLocal: true,
          id:
            photo.id ||
            `photo-${Date.now()}-${Math.random().toString(36).substring(2)}`,
        }));
        state.primaryPhotoIndex = primaryPhotoIndex;
        state.primary_photo_index = primaryPhotoIndex;

      }
      state.step = Math.max(state.step, 6);
    },
    updatePrimaryPhotoIndex: (state, action: PayloadAction<number>) => {
      // Update both primary photo index fields for consistency
      state.primary_photo_index = action.payload;
      state.primaryPhotoIndex = action.payload;
    },
    setSocialSignup: (
      state,
      action: PayloadAction<{
        email: string;
        full_name: string;
        provider: string;
      }>,
    ) => {
      state.email = action.payload.email;
      state.full_name = action.payload.full_name;
      state.isSocialSignup = true;
      state.socialProvider = action.payload.provider;
      state.step = Math.max(state.step, 2);
      
      // Persist OAuth signup data to localStorage for recovery during navigation
      try {
        localStorage.setItem('registration_email', action.payload.email);
        localStorage.setItem('registration_full_name', action.payload.full_name);
        localStorage.setItem('registration_is_social_signup', 'true');
        localStorage.setItem('registration_social_provider', action.payload.provider);
      } catch (e) {
        // Silent fail - localStorage might not be available
      }
    },
    resetRegistration: () => initialState,
  },
});

export const {
  updateEmail,
  updateName,
  updateBirthday,
  updateGender,
  updateOrientation,
  updatePreference,
  updateBio,
  updateLocation,
  updatePhotos,
  updatePrimaryPhotoIndex,
  setSocialSignup,
  resetRegistration,
} = registrationSlice.actions;

export default registrationSlice.reducer;
